import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useState} from "react";
import {REQUEST_ACCESS_MODEL} from "../Model/Model";
import {
    PublicCreateRequest, PublicCreateRequestDocument
} from "../../../../../reduxToolkit/RequestAccess/RequestAccessApi";
import {toast} from "react-toastify";
import {getUrlParameters} from "../../External/Model/Model";
import routes from "../../../../../Routes/routes";
import HandleChange from "../../../../../Helpers/HandleChange";
import {VISITOR_MODEL} from "../../Model/Model";
import {PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg} from "../../../../../Apis/functions";
import {EVENT_VISITOR_MODEL, registerInEvent} from "../../../Events/External/Model/Model";
import { PublicVerifyEmail} from "../../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";

/**
 *
 /**
 * CommonQrRequestAccessApp
 *
 * Hook/Component to manage the QR request access flow for visitors.
 * Handles steps navigation, visitor/employee selection, request reason input,
 * file uploads, and request submission.
 *
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param {Object} userData - Current user/visitor data object.
 * @param {Function} setUserData - Function to update the user data state.
 *
 * @returns {Object} An object containing state variables and handler functions:
 *
 */
const CommonQrRequestAccessApp = ({ userData, setUserData }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {eventId} = useParams();
    const navigate = useNavigate();
    const [steps, setSteps] = useState(null);
    const [activeStep, setActiveStep] = useState(1);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [visitorList, setVisitorList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [emptyFields, setEmptyFields] = useState(false);
    const [photoPickerFile, setPhotoPickerFile] = useState({ ...PHOTO_PICKER_MODEL });
    const [requestAccessItem, setRequestAccessItem] = useState({ ...REQUEST_ACCESS_MODEL });
    const { handleChange } = HandleChange({ model: requestAccessItem, setModel: setRequestAccessItem });
    const [showLoader, setShowLoader] = useState(false);
    const [email, setEmail] = useState('');

    /**
     * Navigate to a specific step in the QR request flow.
     *
     * @param {number} step - The step number to navigate to.
     */
    const navigateStep = (step) => {
        setActiveStep(step);
    }


    /**
     * Validate the request and create a new request access entry.
     * Handles optional file upload after the request is created.
     */
    const handleCreate = () => {
        if (!requestAccessItem?.isValid()) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        let requestAccessDto = requestAccessItem.toRegister();
        requestAccessDto.requestedById = visitorList?.[0]?.id || null;
        requestAccessDto.visitToId = employeeList?.[0]?.id || null;

        if(userData)
            requestAccessDto = {
            ...requestAccessDto,
                ...userData.qrFlowToRegister()
        };

        setShowLoader(true);
        const minDelay = new Promise(resolve => setTimeout(resolve, 1000));
        const createRequest = dispatch(PublicCreateRequest(requestAccessDto));

        Promise.all([createRequest, minDelay]).then(([{ payload: { data: { data, success, code } } }]) => {
            if (success === true) {
                if (photoPickerFile?.listFiles?.length === 0) {
                    successRequest();
                    return;
                }

                const requestAccessId = data?.id;
                createMultiple(requestAccessId);
            }else errorMsg(code);
        }).finally(() => {
            if (photoPickerFile?.listFiles?.length === 0) setShowLoader(false);
        });
    };

    /**
     * Handles QR-based event registration flow using email verification.
     *
     * Flow:
     * - Verifies email via API
     * - If user exists and is validated:
     *   -> Registers user in event
     * - If user exists but is not validated:
     *   -> Redirects to validation flow
     * - If verification fails:
     *   -> Shows error message
     *
     * @returns {void}
     */
    const handleCreateInReservation = () => {
        setShowLoader(true);
        dispatch(PublicVerifyEmail(EVENT_VISITOR_MODEL.toRegisterVerifyEmail({ email, eventId })))
        .then(({ payload: { data: { data, success, code } } }) => {
            const userId = data?.userId;
            if (success && userId){
                if(data?.approvalStatusId === STATUS_MODEL.VALIDATED){
                    registerInEvent({
                        dispatch,
                        resolvedUserId:userId,
                        eventId,
                        setLoading:setShowLoader,
                        t,
                        navigate,
                        successUrl:getUrlParameters({ url: `${routes.eventDetails.successfullyRegistered}${eventId}` })
                    });
                    return;
                }
                navigate(getUrlParameters({url: `${routes.eventDetails.chooseValidationType}${userId}/${eventId}`}));
            }
            else {
                errorMsg(code);
                setShowLoader(false);
            }
        });
    };

    /**
     * Uploads multiple files associated with a request access.
     *
     * Iterates through selected files and uploads each one
     * linked to the created request ID.
     *
     * After completion, shows success notification and finalizes request.
     *
     * @param {string|number} requestAccessId - Request identifier.
     * @returns {Promise<void>}
     */
    const createMultiple = async (requestAccessId) => {
        let files = photoPickerFile.listFiles;

        if (!files || files.length === 0) return;

        await Promise.all(
            files.map((item) => handleUploadFile(requestAccessId, item.fileSelected))
        );

        toast.success(t('has_been_requested_successfully'));
        successRequest();
    };

    /**
     * Upload a document file for a specific request access.
     *
     * @param {string|number} requestAccessId - The ID of the request access.
     * @param {File} uploadFile - The file to upload.
     */
    const handleUploadFile = (requestAccessId, uploadFile) => {
        const formData = new FormData();
        formData.append('fileUploadDto.files', uploadFile);
        formData.append('requestAccessId', requestAccessId);
        formData.append('description', requestAccessItem.requestReason);
        formData.append('fileUploadDto.userFileNames', uploadFile?.name || '');
        formData.append('fileUploadDto.path',  '');

        return dispatch(PublicCreateRequestDocument(formData));
    };

    /**
     * Mark the request as successfully completed,
     * advance to the next step, and reset all data.
     */
    const successRequest = () => {
        setShowLoader(false);
        navigateStep(activeStep + 1);
        resetData();
    }

    /**
     * Navigate back to the main QR request access page.
     */
    const goHome = () => {
        navigate(getUrlParameters({ url: routes.qrRequestAccess.main }))
    }

    /**
     * Reset all state data to its initial values,
     * including user data, request access item, visitor/employee lists, and file picker.
     */
    const resetData = () => {
        if(setUserData) setUserData({...VISITOR_MODEL});
        setRequestAccessItem({...REQUEST_ACCESS_MODEL});
        setVisitorList([]);
        setEmployeeList([]);
        setPhotoPickerFile({ ...PHOTO_PICKER_MODEL });
    }

    return {
        t,
        navigate,
        dispatch,
        btnDisabled,
        setBtnDisabled,
        steps, setSteps,
        activeStep, setActiveStep,
        visitorList, setVisitorList,
        employeeList, setEmployeeList,
        emptyFields, setEmptyFields,
        photoPickerFile, setPhotoPickerFile,
        requestAccessItem, setRequestAccessItem,
        showLoader,
        handleChange,
        handleCreate,
        handleCreateInReservation,
        navigateStep,
        goHome,
        resetData,
        email,
        setEmail
    }

}

export default CommonQrRequestAccessApp
